import { DateTime } from "luxon";

import { IProductionDashboardBayVehicleAssembly } from "../models/ProductionDashboard";

type TimedAssembly = Pick<
  IProductionDashboardBayVehicleAssembly,
  "isComplete" | "workedSeconds" | "startedOn" | "targetMinutes"
>;

/**
 * Seconds of work left on a vehicle, from the targets of the assemblies it hasn't finished:
 * each unfinished assembly contributes its target less whatever has already been worked on it
 * (never negative - running over doesn't buy time back), with a running timer counted live.
 *
 * Null when no unfinished assembly has a target, because "0 left" would read as done. An
 * unfinished assembly without a target is skipped rather than blocking the estimate, so a
 * customer who has only set targets on the big assemblies still gets a figure.
 */
export function estimatedRemainingSeconds(
  assemblies: TimedAssembly[],
  now: DateTime
): number | null {
  let total = 0;
  let anyTarget = false;

  for (const a of assemblies) {
    if (a.isComplete || !a.targetMinutes || a.targetMinutes <= 0) continue;
    anyTarget = true;

    const running = a.startedOn
      ? Math.max(0, now.diff(DateTime.fromISO(a.startedOn, { zone: "utc" }), "seconds").seconds)
      : 0;
    const worked = (a.workedSeconds ?? 0) + running;
    total += Math.max(0, a.targetMinutes * 60 - worked);
  }

  return anyTarget ? Math.round(total) : null;
}

// "~2h 05m" / "~45m" / "<1m" - a rough figure, so no seconds.
export function formatRemaining(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return "<1m";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `~${hours}h ${String(rest).padStart(2, "0")}m` : `~${minutes}m`;
}
